import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DocumentFile } from '@app/interfaces/dm/document';
import {
    AddKanbanBoardProject,
    AddProjectMember,
    AddSwimLane,
    AddTask,
    AddTaskComment,
    AddTaskConfig,
    AddTaskConfigField,
    AddTaskLabel,
    BatchAddTask,
    CategorySwimLane, DashboardResource, DashboardTaskParams, DashboardTester,
    EditKanbanBoardProject,
    EditProjectMemberParams,
    EditSwimLane,
    EditTask,
    EditTaskComment,
    EditTaskConfig,
    EditTaskConfigField,
    EditTaskLabel, ExportProjectSwimLaneParams, ExportTrackerParams,
    KanbanBoardProject,
    KanbanBoardProjectParams,
    KanbanBoardProjectWithPage,
    LinkedTask,
    ProjectTaskParams, QueryTaskParams, ResourceTag,
    SaveConfigFieldOrder, SwimLaneCount,
    Task,
    TaskAllParams,
    TaskComment,
    TaskConfig,
    TaskConfigCategory,
    TaskConfigField,
    TaskContentResponses,
    TaskFieldContent,
    TaskHistory, TaskHistoryPageParams,
    TaskHistoryParams, TaskHistoryWithPage,
    TaskLabel, TaskResource,
    TaskSwimLane, TaskTester,
    TrackerInstance,
    TrackerProject, TrackerResource,
    TrackerTaskParams,
    TrackerTester,
    UpdateLinkTask,
    UpdateSwimLaneOrder,
    UpdateTaskSwimLane,
    UpdateTaskSwimLaneOrder,
} from '@app/interfaces/lm/kanban-board';
import { ResourceInstances } from '@app/interfaces/rm/resource-instances';
import { Account } from '@app/interfaces/sys/account';
import { SelectOption } from '@app/interfaces/sys/dict';
import { environment } from '@env/environment';
import { TicketService } from '@services/lm/ticket.service';
import { AccountService } from '@services/sys/account.service';
import dayjs from 'dayjs';
import { lastValueFrom } from 'rxjs';
import { FileService } from './file.service';

@Injectable({
    providedIn: 'root',
})
export class KanbanBoardService {

    constructor(private http: HttpClient,
        private accountService: AccountService,
        private fileService: FileService,
        private ticketService: TicketService,) {
    }

    private baseUrl = environment.api.services.tm;

    public fieldCategoryTypes: {
        [fileName: string]: string
    } = {
            'short text': 'text',
            paragraph: 'textarea',
            label: 'label',
            timestamp: 'timestamp',
            date: 'date',
            number: 'number',
            dropdown: 'dropdown',
            checkbox: 'checkbox',
            url: 'url',
            resource: 'treeSelect',
            attachment: 'attachment',
            user: 'user',
            tester: 'user'
        };

    /* project api */
    async findProject(params: KanbanBoardProjectParams): Promise<KanbanBoardProjectWithPage> {
        return lastValueFrom(this.http.get<KanbanBoardProjectWithPage>(`${this.baseUrl}/task-project/all`,
            { params: Object.assign(params) }));
    }

    async getProjectOne(id: string): Promise<KanbanBoardProject> {
        return lastValueFrom(this.http.get<KanbanBoardProject>(`${this.baseUrl}/task-project`,
            { params: Object.assign({ id }) }));
    }

    async getKanbanProjectDetail(key: string): Promise<KanbanBoardProject> {
        return lastValueFrom(this.http.get<KanbanBoardProject>(`${this.baseUrl}/task-project/key`, {
            params: {
                key
            }
        }));
    }

    async addKanbanProject(data: AddKanbanBoardProject): Promise<KanbanBoardProject> {
        return lastValueFrom(this.http.post<KanbanBoardProject>(`${this.baseUrl}/task-project`, data));
    }

    async editKanbanProject(data: EditKanbanBoardProject): Promise<KanbanBoardProject> {
        return lastValueFrom(this.http.put<KanbanBoardProject>(`${this.baseUrl}/task-project`, data));
    }

    async removeKanbanProject(id: string): Promise<void> {
        return lastValueFrom(this.http.delete<void>(`${this.baseUrl}/task-project`, {
            params: Object.assign({ id }),
        }));
    }

    async editKanbanProjectMembers(data: EditProjectMemberParams): Promise<void> {
        return lastValueFrom(this.http.put<void>(`${this.baseUrl}/task-project/update-project-member`, data));
    }

    /* swim lane api */
    async getSwimLanesByProjectId(id: string): Promise<TaskSwimLane[]> {
        return lastValueFrom(this.http.get<TaskSwimLane[]>(`${this.baseUrl}/task-swim-lane/all/${id}`));
    }

    async getAllTaskSwimLane(): Promise<CategorySwimLane[]> {
        return lastValueFrom(this.http.get<CategorySwimLane[]>(`${this.baseUrl}/task-swim-lane/all`));
    }

    async getSwimLaneDetail(id: string): Promise<TaskSwimLane> {
        return lastValueFrom(this.http.get<TaskSwimLane>(`${this.baseUrl}/task-swim-lane`, {
            params: Object.assign({
                id
            })
        }));
    }

    async addSwimLane(data: AddSwimLane): Promise<TaskSwimLane> {
        return lastValueFrom(this.http.post<TaskSwimLane>(`${this.baseUrl}/task-swim-lane`, data));
    }

    async editSwimLane(data: EditSwimLane): Promise<void> {
        return lastValueFrom(this.http.put<void>(`${this.baseUrl}/task-swim-lane`, data));
    }

    async removeSwimLane(id: string): Promise<void> {
        return lastValueFrom(this.http.delete<void>(`${this.baseUrl}/task-swim-lane`, {
            params: Object.assign({ id }),
        }));
    }

    async updateSwimLaneOrder(data: UpdateSwimLaneOrder): Promise<TaskSwimLane[]> {
        return lastValueFrom(this.http.post<TaskSwimLane[]>(`${this.baseUrl}/task-swim-lane/change-order`, data));
    }

    /* task label */
    async getAllTaskLabel(): Promise<TaskLabel[]> {
        return lastValueFrom(this.http.get<TaskLabel[]>(`${this.baseUrl}/task-label/all`));
    }


    async addTaskLabel(data: AddTaskLabel): Promise<TaskLabel> {
        return lastValueFrom(this.http.post<TaskLabel>(`${this.baseUrl}/task-label`, data));
    }

    async editTaskLabel(data: EditTaskLabel): Promise<TaskLabel> {
        return lastValueFrom(this.http.put<TaskLabel>(`${this.baseUrl}/task-label`, data));
    }

    async removeTaskLabel(id: string): Promise<void> {
        return lastValueFrom(this.http.delete<void>(`${this.baseUrl}/task-label`, {
            params: Object.assign({ id }),
        }));
    }


    /* task config */
    async getAllTaskConfig(): Promise<TaskConfig[]> {
        return lastValueFrom(this.http.get<TaskConfig[]>(`${this.baseUrl}/task-config/all`));
    }

    async addTaskConfig(data: AddTaskConfig): Promise<TaskConfig> {
        return lastValueFrom(this.http.post<TaskConfig>(`${this.baseUrl}/task-config`, data));
    }

    async editTaskConfig(data: EditTaskConfig): Promise<TaskConfig> {
        return lastValueFrom(this.http.put<TaskConfig>(`${this.baseUrl}/task-config`, data));
    }

    async removeTaskConfig(id: string): Promise<void> {
        return lastValueFrom(this.http.delete<void>(`${this.baseUrl}/task-config`, {
            params: Object.assign({ id }),
        }));
    }

    async getTaskConfigDetail(id: string): Promise<TaskConfig> {
        return lastValueFrom(this.http.get<TaskConfig>(`${this.baseUrl}/task-config`, {
            params: Object.assign({ id }),
        }));
    }

    async createTaskConfigField(data: AddTaskConfigField): Promise<TaskConfigField> {
        return lastValueFrom(this.http.post<TaskConfigField>(`${this.baseUrl}/task-config-field`, data));
    }

    async batchCreateTaskConfigField(data: { creates: AddTaskConfigField[] }): Promise<TaskConfigField[]> {
        return lastValueFrom(this.http.post<TaskConfigField[]>(`${this.baseUrl}/task-config-field/batch`, data));
    }

    async changeConfigFieldOrder(data: SaveConfigFieldOrder): Promise<void> {
        return lastValueFrom(this.http.post<void>(`${this.baseUrl}/task-config-field/change-order`, data));
    }

    async editTaskConfigField(data: EditTaskConfigField): Promise<void> {
        return lastValueFrom(this.http.put<void>(`${this.baseUrl}/task-config-field`, data));
    }

    async removeTaskConfigField(id: string): Promise<void> {
        return lastValueFrom(this.http.delete<void>(`${this.baseUrl}/task-config-field`, {
            params: Object.assign({ id }),
        }));
    }

    async getAllCategory(): Promise<TaskConfigCategory[]> {
        return lastValueFrom(this.http.get<TaskConfigCategory[]>(`${this.baseUrl}/task-config-field/get-all-category`));
    }

    async getTaskConfigFieldAll(configId: string): Promise<TaskConfigField[]> {
        return lastValueFrom(this.http.get<TaskConfigField[]>(`${this.baseUrl}/task-config-field/all`, {
            params: Object.assign({ configId }),
        }));
    }

    /* task api */
    async getTasksByProject(params: ProjectTaskParams): Promise<Task[]> {
        return lastValueFrom(this.http.get<Task[]>(`${this.baseUrl}/task/all/project`, {
            params: Object.assign(params)
        }));
    }

    async getTaskAll(params?: TaskAllParams): Promise<Task[]> {
        return lastValueFrom(this.http.get<Task[]>(`${this.baseUrl}/task/all`, {
            params: params ? Object.assign(params) : {}
        }));
    }

    async loadAllTasks(params: QueryTaskParams): Promise<Task[]> {
        return lastValueFrom(this.http.post<Task[]>(`${this.baseUrl}/task/all/query/kanban`, params));
    }

    async loadAllTasksByFloor(params: QueryTaskParams): Promise<Task[]> {
        return lastValueFrom(this.http.post<Task[]>(`${this.baseUrl}/task/all/query/floormap`, params));
    }

    async loadAllTasksByProject(params: TrackerTaskParams): Promise<TrackerProject[]> {
        return lastValueFrom(this.http.post<TrackerProject[]>(`${this.baseUrl}/task/all/query/tracker/project`, params));
    }

    async loadAllTasksByTester(params: TrackerTaskParams): Promise<TrackerTester[]> {
        return lastValueFrom(this.http.post<TrackerTester[]>(`${this.baseUrl}/task/all/query/tracker/tester`, params));
    }

    async loadAllTasksByResource(params: TrackerTaskParams): Promise<TrackerResource[]> {
        return lastValueFrom(this.http.post<TrackerResource[]>(`${this.baseUrl}/task/all/query/tracker/resource`, params));
    }

    async loadAllTasksByInstance(params: TrackerTaskParams): Promise<TrackerInstance[]> {
        return lastValueFrom(this.http.post<TrackerInstance[]>(`${this.baseUrl}/task/all/query/tracker/resource-group`, params));
    }

    async getSubTaskAll(params?: TaskAllParams): Promise<Task[]> {
        return lastValueFrom(this.http.get<Task[]>(`${this.baseUrl}/task-subtask/all`, {
            params: params ? Object.assign(params) : {}
        }));
    }

    async getTaskDetail(taskId: string): Promise<Task> {
        return lastValueFrom(this.http.get<Task>(`${this.baseUrl}/task`, {
            params: Object.assign({
                id: taskId
            })
        }));
    }

    async getTaskDetailByKey(taskKey: string, hideErrorMsg: boolean = false): Promise<Task> {
        return lastValueFrom(this.http.get<Task>(`${this.baseUrl}/task/task-key`, {
            headers: {
                hideErrorMessage: `${hideErrorMsg}`
            },
            params: {
                taskKey
            }
        }));
    }

    getTaskSummaryByTester(params: TrackerTaskParams) {
        return lastValueFrom(this.http.post<TrackerProject[]>(`${this.baseUrl}/task/all/query/tracker/tester/summary`, params));
    }

    getTaskSummaryByResource(params: TrackerTaskParams) {
        return lastValueFrom(this.http.post<TrackerProject[]>(`${this.baseUrl}/task/all/query/tracker/resource/summary`, params));
    }

    async addTask(data: AddTask): Promise<Task> {
        return lastValueFrom(this.http.post<Task>(`${this.baseUrl}/task`, data));
    }

    async batchAddTask(data: BatchAddTask): Promise<BatchAddTask> {
        return lastValueFrom(this.http.post<BatchAddTask>(`${this.baseUrl}/task/batch`, data));
    }

    transformTaskToPayload(task: any, project: KanbanBoardProject): any {
        const formatDate = (date: any) => date ? new Date(date).toISOString() : null;
        const now = new Date();

        function processIntervals(intervals: any[]) {
            if (!Array.isArray(intervals)) return [];
            return intervals.map(interval => {
                let { startDate, endDate } = interval;
                let start = startDate ? new Date(startDate) : null;
                let due = endDate ? new Date(endDate) : null;

                if (due && due > now) {
                    due = new Date(now);
                }

                if (start && start > now && due) {
                    start = new Date(due.getTime() - 1000);
                }

                return {
                    startDate: formatDate(start),
                    endDate: formatDate(due)
                };
            });
        }

        // 处理 resources
        const resources = (task.resources || []).map((r: {
            timeIntervals: {
                dueDate: null; startDate: any;
            }[]; hardwareId: any; hardwareType: any; name: any; resourceInstanceGroupId: any; resourceInstanceId: any;
        }) => {
            let timeIntervals = r.timeIntervals || [];
            let processedIntervals = processIntervals(timeIntervals);
            let firstInterval = processedIntervals[0] || {};
            return {
                endDate: firstInterval.endDate || null,
                startDate: firstInterval.startDate || null,
                hardwareId: r.hardwareId,
                hardwareType: r.hardwareType,
                name: r.name,
                resourceInstanceGroupId: r.resourceInstanceGroupId,
                resourceInstanceId: r.resourceInstanceId,
                timeIntervals: processedIntervals
            };
        });

        const testers = (task.testers || []).map((t: {
            testerId: any; testerName: any; timeIntervals: {
                startDate(startDate: any): unknown; dueDate: any;
            }[];
        }) => {
            let timeIntervals = t.timeIntervals || [];
            let processedIntervals = processIntervals(timeIntervals);
            let firstInterval = processedIntervals[0] || {};
            return {
                testerId: t.testerId,
                testerName: t.testerName,
                startDate: firstInterval.startDate || null,
                endDate: firstInterval.endDate || null,
                timeIntervals: processedIntervals
            };
        });

        let startDate = task.startDate ? new Date(task.startDate) : null;
        let dueDate = task.dueDate ? new Date(task.dueDate) : null;
        if (dueDate && dueDate > now) {
            dueDate = new Date(now);
        }
        if (startDate && startDate > now) {
            startDate = new Date(now.getTime() - 1000);
        }

        return {
            assignees: task.assignees || [],
            assigneesInfo: task.assigneesInfo || [],
            configIcon: task.configIcon,
            configId: task.configId,
            configName: task.configName,
            description: task.description,
            startDate: formatDate(startDate),
            dueDate: formatDate(dueDate),
            hasSubtask: task.hasSubtask,
            id: task.taskId ? task.taskId : task.subTaskId,
            labels: Array.isArray(task.labels) && task.labels.length > 0 && typeof task.labels[0] === 'object'
                ? task.labels.map((l: any) => l.id)
                : (task.labels || []),
            linkedTasks: task.linkedTasks || [],
            priority: task.priority,
            projectId: project.id,
            projectKey: project.key,
            projectName: project.projectName,
            resources,
            swimLaneId: task.swimLaneId,
            swimLaneName: task.swimLaneName,
            taskContentResponses: (task.taskContentResponses || []).map((f: { value: string; }) => ({
                ...f,
                value: f.value === "[]" ? [] : f.value
            })),
            taskId: task.taskId,
            taskKey: task.taskKey,
            taskKeyId: task.taskKeyId,
            taskName: task.taskName,
            testers,
            testersInfo: task.testersInfo || [],
            userOrder: Number(task.userOrder?.toFixed(6))
        };
    }


    async releaseAllResource(task: any, targetSwimLaneId: string, projectKey?: string): Promise<void> {
        if (task.subTaskId ) {
            const subTask = await this.getTaskDetailByKey(task.taskKey!);
            const project = await this.getKanbanProjectDetail(projectKey ? projectKey : task.projectKey);
            const taskPayload = this.transformTaskToPayload(subTask, project);
            const params = taskPayload;
            await this.editSubTask(params as EditTask);
            const swimlaneParams = {
                taskId: params.id,
                swimLaneId: targetSwimLaneId
            };
            await this.updateSubTaskSwimLane(swimlaneParams);
        }
        else {
            const taksInfo = await this.getTaskDetailByKey(task.taskKey!);
            const project = await this.getKanbanProjectDetail(projectKey ? projectKey : taksInfo.projectKey);
            const taskPayload = this.transformTaskToPayload(taksInfo, project);
            const params = taskPayload;
            await this.editTask(params as EditTask);
            const swimlaneParams = {
                taskId: params.id,
                swimLaneId: targetSwimLaneId
            };
            await this.updateTaskSwimLane(swimlaneParams);
        }

    }

    async editTask(data: EditTask): Promise<Task> {
        return lastValueFrom(this.http.put<Task>(`${this.baseUrl}/task`, data));
    }

    async updateTaskSwimLane(params: UpdateTaskSwimLane): Promise<void> {
        return lastValueFrom(this.http.post<void>(`${this.baseUrl}/task/update-swim-lane`, {}, {
            params: Object.assign(params),
        }));
    }

    async updateTaskSwimLaneOrder(data: UpdateTaskSwimLaneOrder): Promise<void> {
        return lastValueFrom(this.http.post<void>(`${this.baseUrl}/task/update-swim-lane/order`, data));
    }

    async updateTaskLinkedTask(data: UpdateLinkTask): Promise<void> {
        return lastValueFrom(this.http.post<void>(`${this.baseUrl}/task/update-link-task`, data));
    }

    async removeTask(id: string): Promise<void> {
        return lastValueFrom(this.http.delete<void>(`${this.baseUrl}/task`, {
            params: Object.assign({ id }),
        }));
    }


    /* sub tasks */
    async getTaskAllSubTask(params: {
        parentTaskId: string,
        showArchived?: boolean
    }): Promise<Task[]> {
        return lastValueFrom(this.http.get<Task[]>(`${this.baseUrl}/task-subtask/all/parent`, {
            params: Object.assign(params)
        }));
    }

    async getSubTaskDetail(subTaskId: string): Promise<Task> {
        return lastValueFrom(this.http.get<Task>(`${this.baseUrl}/task-subtask`, {
            params: Object.assign({
                id: subTaskId
            })
        }));
    }

    async addSubTask(data: AddTask): Promise<Task> {
        return lastValueFrom(this.http.post<Task>(`${this.baseUrl}/task-subtask`, data));
    }

    editSubTask(data: EditTask): Promise<Task> {
        return lastValueFrom(this.http.put<Task>(`${this.baseUrl}/task-subtask`, data));
    }

    removeSubTask(id: string): Promise<void> {
        return lastValueFrom(this.http.delete<void>(`${this.baseUrl}/task-subtask`, {
            params: Object.assign({ id }),
        }));
    }


    async updateSubTaskSwimLane(params: UpdateTaskSwimLane): Promise<void> {
        return lastValueFrom(this.http.post<void>(`${this.baseUrl}/task-subtask/update-swim-lane`, {}, {
            params: Object.assign(params),
        }));
    }

    /* task history api */
    async getTaskHistory(params: TaskHistoryParams): Promise<TaskHistory[]> {
        return lastValueFrom(this.http.get<TaskHistory[]>(`${this.baseUrl}/task-history/all`, {
            params: Object.assign(params)
        }));
    }

    async getTaskHistoryPage(params: TaskHistoryPageParams): Promise<TaskHistoryWithPage> {
        return lastValueFrom(this.http.get<TaskHistoryWithPage>(`${this.baseUrl}/task-history`, {
            params: Object.assign(params)
        }));
    }

    /* task comment api */
    async getTaskComment(taskId: string): Promise<TaskComment[]> {
        return lastValueFrom(this.http.get<TaskComment[]>(`${this.baseUrl}/task-comment/all-task-id`, {
            params: Object.assign({
                taskId
            })
        }));
    }

    async getCommentDetail(id: string): Promise<TaskComment> {
        return lastValueFrom(this.http.get<TaskComment>(`${this.baseUrl}/task-comment`, {
            params: Object.assign({
                id
            })
        }));
    }

    async addComment(data: AddTaskComment): Promise<TaskComment> {
        return lastValueFrom(this.http.post<TaskComment>(`${this.baseUrl}/task-comment`, data));
    }

    async editComment(data: EditTaskComment): Promise<TaskComment> {
        return lastValueFrom(this.http.put<TaskComment>(`${this.baseUrl}/task-comment`, data));
    }

    async removeComment(id: string, isSubtask: boolean): Promise<void> {
        return lastValueFrom(this.http.delete<void>(`${this.baseUrl}/task-comment`, {
            params: Object.assign({ id, isSubtask }),
        }));
    }

    exportInfo(data: ExportTrackerParams): void {
        let params = new HttpParams();
        for (const key in data) {
            const value = data[key as keyof ExportTrackerParams];
            if (value) {
                params = params.set(key, value);
            }
        }
        this.http.get(`${this.baseUrl}/task/export/query/tracker`, {
            params,
            responseType: 'json',
            observe: 'response',
        }).subscribe({
            next: (response) => {
                if (response.body) {
                    this.ticketService.exportFile(data.view, response.body as string);
                } else {
                    console.error('No file content returned');
                }
            },
            error: (error) => {
                console.error('Error retrieving the file: ', error);
            }
        });
    }

    exportProjectSwimLaneInfo(data: ExportProjectSwimLaneParams, projectName: string): void {
        let params = new HttpParams();
        for (const key in data) {
            const value = data[key as keyof ExportProjectSwimLaneParams];
            if (value) {
                params = params.set(key, value);
            }
        }
        this.http.get(`${this.baseUrl}/task/export/query/tracker/project`, {
            params,
            responseType: 'json',
            observe: 'response',
        }).subscribe({
            next: (response) => {
                if (response.body) {
                    this.ticketService.exportFile(`${projectName}_${data.swimLaneName}`, response.body as string);
                } else {
                    console.error('No file content returned');
                }
            },
            error: (error) => {
                console.error('Error retrieving the file: ', error);
            }
        });
    }

    /* other */
    getMemberRole(): string[] {
        return ['Member', 'Owner'];
    }

    formatTasks(arr: Task[], accountList: Account[]): Task[] {
        // 创建 userMappingWithAvatar 对象，包含用户的 label 和 avatar 信息
        const userMappingWithAvatar: Record<string, { label: string, enable: boolean, avatar: string | undefined }> = {};
        accountList.forEach(it => {
            userMappingWithAvatar[it.id] = {
                label: this.accountService.getUserName(it),
                enable: it.enable,
                avatar: it?.avatar || undefined  // 将 null 替换为 undefined
            };
        });

        return arr.map(it => {
            const { testers, assignees, labels, linkedTasks } = it;
            const testersInfo = (testers || []).map(it => {
                const testerId = it.testerId as number;
                return {
                    label: it?.testerName || '',
                    value: testerId,
                    avatar: testerId && userMappingWithAvatar[testerId] ? userMappingWithAvatar[testerId] : undefined
                } as SelectOption;
            });
            const assigneesInfo = this.getValidUsersNameWithAvatar(assignees, userMappingWithAvatar);

            // 格式化 linked tasks
            const formattedLinkedTasks = linkedTasks ? linkedTasks.map(linkedTask => this.formatLinkedTask(linkedTask, userMappingWithAvatar)) : [];

            return {
                ...it,
                testersInfo,
                assigneesInfo,
                labelIds: (labels || []).map(it => it.id),
                linkedTasks: formattedLinkedTasks
            };
        });
    }

    getValidUsersNameWithAvatar(userIds: number[], userMapping: Record<string, { label: string, enable: boolean, avatar: string | undefined }>): { label: string; value: number; avatar: string | undefined }[] {
        if (!Array.isArray(userIds)) {
            return [];
        }

        return userIds
            .filter(id => userMapping[id])
            .map(id => ({
                label: userMapping[id].label,
                enable: userMapping[id].enable,
                value: id,
                avatar: userMapping[id].avatar,
            }));
    }


    formatLinkedTask(linkedTask: LinkedTask, userMapping: Record<string, { label: string, enable: boolean, avatar: string | undefined }>): LinkedTask {
        const validAssignees = linkedTask.assignees.filter(assigneeId => assigneeId !== '');

        const assigneesInfo = validAssignees.length > 0
            ? validAssignees.map(assigneeId => ({
                label: userMapping[assigneeId]?.label || assigneeId,
                enable: userMapping[assigneeId]?.enable ?? true,
                value: assigneeId,
                avatar: userMapping[assigneeId]?.avatar || undefined,
            }))
            : undefined;

        return {
            ...linkedTask,
            ...(assigneesInfo && { assigneesInfo })
        };
    }

    getTaskPriorityOptions(): SelectOption[] {
        return [
            { label: 'Low', value: 'Low' },
            { label: 'Medium', value: 'Medium' },
            { label: 'High', value: 'High' },
            { label: 'Critical', value: 'Critical' },
        ];
    }

    getCategoryIcon(name: string): string {
        return this.fieldCategoryTypes[name.toLowerCase()] || 'text';
    }

    // Task 默认数据
    getTaskDefaultFields(): {
        fieldName: string,
        categoryName: string,
        panel: string,
        defaultValueRule: string,
        isRestrained?: boolean
        isRequired?: boolean
    }[] {
        return [
            {
                fieldName: 'Task Name',
                categoryName: 'Short Text',
                panel: 'left',
                isRequired: true,
                defaultValueRule: JSON.stringify({
                    fieldKey: 'taskName',
                    fieldType: 'DEFAULT_FORM'
                })
            },
            {
                fieldName: 'Type',
                categoryName: 'DropDown',
                panel: 'left',
                isRequired: true,
                isRestrained: true,
                defaultValueRule: JSON.stringify({
                    fieldKey: 'configId',
                    fieldType: 'DEFAULT_FORM'
                })
            },
            {
                fieldName: 'Description',
                categoryName: 'Paragraph',
                panel: 'left',
                defaultValueRule: JSON.stringify({
                    fieldKey: 'description',
                    fieldType: 'DEFAULT_FORM'
                })
            },
            {
                fieldName: 'Attachment',
                categoryName: 'Attachment',
                panel: 'left',
                defaultValueRule: JSON.stringify({
                    fieldKey: 'attachment',
                    fieldType: 'DEFAULT_FORM'
                })
            },
            // {
            //     fieldName: 'Tester',
            //     categoryName: 'User',
            //     panel: 'left',
            //     defaultValueRule: JSON.stringify({
            //         fieldKey: 'tester',
            //         fieldType: 'DEFAULT_FORM'
            //     })
            // },
            {
                fieldName: 'Parent',
                categoryName: 'DropDown',
                panel: 'left',
                isRestrained: true,
                defaultValueRule: JSON.stringify({
                    fieldKey: 'parentTaskId',
                    fieldType: 'DEFAULT_FORM'
                })
            },
            {
                fieldName: 'Linked Tasks',
                categoryName: 'DropDown',
                panel: 'left',
                isRestrained: false,
                defaultValueRule: JSON.stringify({
                    fieldKey: 'linkedTaskIds',
                    fieldType: 'DEFAULT_FORM'
                })
            },
            {
                fieldName: 'Status',
                categoryName: 'DropDown',
                panel: 'right_top',
                isRestrained: true,
                isRequired: true,
                defaultValueRule: JSON.stringify({
                    fieldKey: 'swimLaneId',
                    fieldType: 'DEFAULT_FORM'
                })
            },
            {
                fieldName: 'Start Date',
                categoryName: 'Date',
                panel: 'right_top',
                isRequired: true,
                defaultValueRule: JSON.stringify({
                    fieldKey: 'startDate',
                    fieldType: 'DEFAULT_FORM'
                })
            },
            {
                fieldName: 'Due Date',
                categoryName: 'Date',
                panel: 'right_top',
                isRequired: true,
                defaultValueRule: JSON.stringify({
                    fieldKey: 'dueDate',
                    fieldType: 'DEFAULT_FORM'
                })
            },
            {
                fieldName: 'Priority',
                categoryName: 'DropDown',
                panel: 'right_top',
                isRestrained: true,
                defaultValueRule: JSON.stringify({
                    fieldKey: 'priority',
                    fieldType: 'DEFAULT_FORM'
                })
            },
            {
                fieldName: 'Label',
                categoryName: 'Label',
                panel: 'right_top',
                defaultValueRule: JSON.stringify({
                    fieldKey: 'labels',
                    fieldType: 'DEFAULT_FORM'
                })
            },
            {
                fieldName: 'Assignee',
                categoryName: 'User',
                panel: 'right_top',
                isRestrained: false,
                defaultValueRule: JSON.stringify({
                    fieldKey: 'assignees',
                    fieldType: 'DEFAULT_FORM'
                }),
                isRequired: true
            },
            // {
            //     fieldName: 'Tester',
            //     categoryName: 'DropDown',
            //     panel: 'right_top',
            //     defaultValueRule: JSON.stringify({
            //         fieldKey: 'testers',
            //         fieldType: 'DEFAULT_FORM'
            //     })
            // }
        ];
    }

    // 判断是否为Task固定字段
    isDefaultField(defaultValueRule?: string): boolean {
        let config: any = {};
        try {
            config = defaultValueRule ? JSON.parse(defaultValueRule) : {};
        } catch (e) {
            config = {};
        }
        return config.fieldType === 'DEFAULT_FORM';
    }

    // 编辑task获取task参数
    getTaskParams(taskInfo: Task): EditTask {
        const { parentTaskId } = taskInfo;
        const params = {
            ...taskInfo,
            id: parentTaskId ? taskInfo.subTaskId : taskInfo.taskId,
            labels: taskInfo.labels.map(it => it.id),
        } as EditTask;
        return params;
    }

    formatFileList(arr: DocumentFile[]): string[] {
        return (arr || []).map(it => {
            const { id, name, type, size, createdDate } = it;
            return JSON.stringify({
                fileId: id,
                fileName: name,
                fileType: type,
                fileSize: size,
                createdDate
            });
        });
    }

    translateArrayToObj(arr: any, key: string = 'fieldName'): any {
        let obj = {};
        (arr || []).forEach((it: any) => {
            const valueKey = it[key];
            obj = {
                ...obj,
                [valueKey]: it,
            };
        });
        return obj;
    }

    /* 格式化自定义字段的提交数据 */
    formatTaskFieldValue(config: TaskConfigField, value: any): string | string[] {
        const { categoryIsPlural } = config;
        const valueIsArray = Array.isArray(value);
        // 复数类型的category
        if (categoryIsPlural) {
            return value ? (valueIsArray ? value : [value]) : [];
        } else {
            return value ? (valueIsArray ? value[0] || '' : value || '') : '';
        }
    }

    /** 获取taskFieldContent */
    getTaskFieldContents(editField: TaskConfigField, value: any, templateFields: TaskConfigField[], taskContentResponses: TaskContentResponses[]): TaskFieldContent[] {
        const editData = this.translateArrayToObj(taskContentResponses, 'taskConfigFieldId');
        const taskFieldContents: TaskFieldContent[] = [];
        const editFieldId = editField.id;
        templateFields.forEach(it => {
            const { id, defaultValueRule, categoryName } = it;
            const isDefaultField = this.isDefaultField(defaultValueRule);
            if (!id || !editFieldId || (isDefaultField && categoryName !== 'Attachment') || categoryName === 'Resource' || categoryName === 'Tester') {
                return;
            }
            const fieldValue = editData?.[id]?.value;
            taskFieldContents.push({
                taskConfigFieldId: id,
                value: this.formatTaskFieldValue(it, id === editFieldId ? value : fieldValue),
            });
        });
        return taskFieldContents;
    }

    /** 获取projectMembers */
    getMembers(projectMemberIds: Array<number | string>, accountList: Account[], showEmail: boolean = true): SelectOption[] {
        const accounts = accountList.filter(it => projectMemberIds.includes(it.id.toString()));
        return this.accountService.formatAccount(accounts, showEmail);
    }

    /** 根据Account获取Account Options */
    getAccountOptions(accountList: Account[], showEmail: boolean = true): SelectOption[] {
        return accountList.map(it => {
            return {
                value: it.id,
                label: this.accountService.getUserName(it, showEmail),
                avatar: this.getAvatarImage(it.avatar as string),
                info: it
            };
        });
    }

    /** 格式化展示资源Tag */
    getResourceTags(resources: Array<ResourceInstances | TaskResource>, notIncludeTypes: string[] = []): ResourceTag[] {
        if (!resources || resources.length === 0) {
            return [];
        }
        const uniqueResources = Array.from(new Map(resources.map(it => [it.hardwareId, it])).values());
        const arr = notIncludeTypes.length > 0 ? uniqueResources
            .filter(it => !notIncludeTypes.includes(it.hardwareType)) : uniqueResources;
        const hardwareTypes = [...new Set(arr.map(it => it.hardwareType))];
        const resourceTags = hardwareTypes.map((hardwareType, index) => {
            const allResources = uniqueResources.filter(it => it.hardwareType === hardwareType);
            return {
                hardwareType,
                resourceNames: allResources.map(it => it.name).join(','),
                color: this.getResourceTagColor(index),
            };
        });
        return resourceTags;
    }

    getResourceTagColor(index: number): string {
        const colors = ['#125082', '#3a0e72', '#BD5FFC', '#FC5F77', '#80aa65', '#473C9D'];
        return colors[index % colors.length];
    }

    getAvatarImage(avatar: string): string {
        if (!avatar || avatar.startsWith('http')) {
            return avatar || '';
        }
        return this.fileService.getPath(avatar);
    }

    extractUniqueCounts(data: Task[]): {
        uniqueCountsMap: Map<string, SwimLaneCount>,
        count: number
    } {
        const uniqueCountsMap = new Map<string, SwimLaneCount>();
        let count = 0;
        data.forEach(task => {
            task.count?.forEach(countItem => {
                if (!uniqueCountsMap.has(countItem.swimLaneId)) {
                    uniqueCountsMap.set(countItem.swimLaneId, countItem);
                    count += countItem.count;
                }
            });
        });
        return {
            uniqueCountsMap,
            count
        };
    }

    async getChamberReservation(params: {
        date: string
    }): Promise<DashboardResource[]> {
        return lastValueFrom(this.http.get<any>(`${this.baseUrl}/task/all/query/dashboard/resources`,
            { params: Object.assign(params) }));
    }

    async getTesterReservation(params: {
        date: string
    }): Promise<DashboardTester[]> {
        return lastValueFrom(this.http.get<any>(`${this.baseUrl}/task/all/query/dashboard/testers`,
            { params: Object.assign(params) }));
    }

    async getTaskByTester(params: DashboardTaskParams): Promise<Task[]> {
        return lastValueFrom(this.http.get<any>(`${this.baseUrl}/task/by-tester`,
            { params: Object.assign(params) }));
    }

    async getTaskByAssignee(params: DashboardTaskParams): Promise<Task[]> {
        return lastValueFrom(this.http.get<any>(`${this.baseUrl}/task/by-assignee`,
            { params: Object.assign(params) }));
    }

    dashboardTaskSort(data: Task[], type: string = 'table'): Task[] {
        const arr = JSON.parse(JSON.stringify(data));
        arr?.sort((obj1: Task, obj2: Task): number => {
            const start1 = dayjs(obj1.startDate).valueOf();
            const start2 = dayjs(obj2.startDate).valueOf();
            return type === 'table' ? (start1 - start2) : (start2 - start1);
        });
        return arr;
    }

    getTaskViewMode(): SelectOption[] {
        return [
            {
                label: 'Assignee',
                value: 'ASSIGNEE'
            },
            {
                label: 'Tester',
                value: 'TESTER'
            }
        ];
    }

}
